import React, { useEffect, useMemo, useState } from "react";
import { getSpendAnalytics, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { BarChart3, Loader2, TrendingUp } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const PALETTE = ["#6366f1", "#22d3ee", "#f59e0b", "#10b981", "#ec4899", "#a78bfa", "#f97316", "#94a3b8"];
const TOP_N = 6;

const monthLabel = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleString("en-US", { month: "short", year: "2-digit" });
};

const compact = (v) => new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v);

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + (p.value || 0), 0);
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-xl">
      <div className="font-medium mb-1">{label}</div>
      {payload.filter((p) => p.value).map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: p.fill || p.color }} />{p.name}</span>
          <span className="font-mono">{money(p.value)}</span>
        </div>
      ))}
      <div className="mt-1 border-t border-border pt-1 flex justify-between font-mono font-semibold"><span>Total</span><span>{money(total)}</span></div>
    </div>
  );
};

export const SpendCharts = () => {
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    getSpendAnalytics(scope, 6).then(setData);
  }, [scope]);

  const { series, keys, names } = useMemo(() => {
    if (!data) return { series: [], keys: [], names: {} };
    const top = data.categories.slice(0, TOP_N);
    const topCodes = top.map((c) => c.gl_code);
    const names = Object.fromEntries(data.categories.map((c) => [c.gl_code, `${c.gl_code} ${c.gl_name}`]));
    const hasOther = data.categories.length > TOP_N;
    const series = data.months.map((m) => {
      const row = { month: monthLabel(m.month) };
      let other = 0;
      Object.entries(m.by_category).forEach(([code, amt]) => {
        if (topCodes.includes(code)) row[code] = amt;
        else other += amt;
      });
      if (hasOther) row.other = Math.round(other * 100) / 100;
      return row;
    });
    const keys = hasOther ? [...topCodes, "other"] : topCodes;
    if (hasOther) names.other = "Other";
    return { series, keys, names };
  }, [data]);

  const topCats = data ? data.categories.slice(0, 8) : [];
  const max = topCats[0]?.amount || 1;

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="spend-charts">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <TrendingUp className="h-4 w-4 text-primary" /> Spend Trends
          {data && <span className="text-xs text-muted-foreground font-mono" data-testid="spend-grand-total">· {money(data.grand_total)} coded</span>}
        </div>
        <div className="flex rounded-lg border border-border p-0.5 text-xs">
          {[["all", "All invoices"], ["approved", "Approved only"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`spend-scope-${k}`}
              onClick={() => setScope(k)}
              className={cn("rounded-md px-3 py-1 font-medium transition-colors", scope === k ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground")}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : data.categories.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground" data-testid="spend-empty">No coded spend yet for this view.</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-5 p-5">
          <div className="lg:col-span-3">
            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Monthly spend by GL category (last 6 months)</div>
            <div className="h-[260px]" data-testid="monthly-spend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="currentColor" className="text-border" strokeOpacity={0.4} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip content={<TooltipBox />} cursor={{ fill: "currentColor", fillOpacity: 0.05 }} />
                  <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: 11 }} formatter={(v) => names[v] || v} />
                  {keys.map((k, i) => (
                    <Bar key={k} dataKey={k} name={k} stackId="spend" fill={PALETTE[i % PALETTE.length]} radius={i === keys.length - 1 ? [4, 4, 0, 0] : 0} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs text-muted-foreground mb-3">Top GL categories to date</div>
            <ul className="space-y-2.5" data-testid="top-categories">
              {topCats.map((c, i) => (
                <li key={c.gl_code} data-testid={`top-category-${c.gl_code}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="truncate"><span className="font-mono font-semibold">{c.gl_code}</span> <span className="text-muted-foreground">{c.gl_name}</span></span>
                    <span className="font-mono font-medium shrink-0 ml-2">{money(c.amount)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(3, (c.amount / max) * 100)}%`, background: PALETTE[i % PALETTE.length] }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
